import * as React from "react";
import {
    IAvailabilitySchedule,
    IClinician,
    IIntakeAvailability,
} from "intake_shared/src/types";
import moment from "../../../../utils/app_moment";
import { getStringFromScheduleDate, sortSchedules } from "../../../../utils/utils";
import ScheduleBox from "../ScheduleBox/ScheduleBox";
import styles from "./Calendar.module.scss";
import { cn } from "../../../helpers";
import EditScheduleDialog from "../EditScheduleDialog/EditScheduleDialog";

interface CalendarProps {
    schedules: IAvailabilitySchedule[];
    onRemoveSchedule: (schedule: IAvailabilitySchedule) => void;
    onRemoveOfficeWindow?: (schedule: IAvailabilitySchedule) => void;
    onUpdateSchedule: (schedule: IAvailabilitySchedule) => void;
    onSelectSchedule?: (
        schedule: IAvailabilitySchedule
    ) => Promise<IAvailabilitySchedule[]>;
    onUpdateCaseLoad?: (() => Promise<void> | undefined) | undefined;
    loggedInClinician: IClinician | undefined;
    officeSchedulesInReview?: IAvailabilitySchedule[];
    setRefreshKey?: (value: React.SetStateAction<number>) => void;
    // Intake session handlers
    onEditIntakeSession?: (schedule: IAvailabilitySchedule) => void;
    intakeSessionAvailability?: IIntakeAvailability[];
}

export default function Calendar({
    schedules,
    onRemoveSchedule,
    onRemoveOfficeWindow,
    onUpdateSchedule,
    onSelectSchedule,
    onUpdateCaseLoad,
    loggedInClinician,
    officeSchedulesInReview,
    setRefreshKey,
    onEditIntakeSession,
    intakeSessionAvailability = [],
}: CalendarProps) {
    const [scheduleToEdit, setScheduleToEdit] =
        React.useState<IAvailabilitySchedule | null>(null);

    const allSchedulesByDay: { [key: string]: IAvailabilitySchedule[] } = {
        sun: [],
        mon: [],
        tue: [],
        wed: [],
        thur: [],
        fri: [],
        sat: [],
    };

    const daysOfWeek = Object.keys(allSchedulesByDay);

    const visibleSchedules = schedules.filter((s) => s.isVisible);

    for (const s of visibleSchedules) {
        const start = moment(getStringFromScheduleDate(s.start));
        try {
            allSchedulesByDay[daysOfWeek[start.day()]].push(s);
        } catch (e) {
            console.error(e);
        }
    }

    const longestArray = Object.values(allSchedulesByDay).reduce((a, b) =>
        a.length > b.length ? a : b
    );
    const maxItems = longestArray.length;

    const paddedSchedulesByDay = Object.fromEntries(
        Object.entries(allSchedulesByDay).map(([day, schedules]) => {
            const sortedSchedules = sortSchedules(schedules);
            const paddedArray: (IAvailabilitySchedule | null)[] = [
                ...sortedSchedules,
            ];
            while (paddedArray.length < maxItems) {
                paddedArray.push(null);
            }
            return [day, paddedArray];
        })
    ) as { [key: string]: (IAvailabilitySchedule | null)[] };

    const getScheduleListGroupByWeekday = (weekDay: string) => {
        const daySchedules = paddedSchedulesByDay[weekDay];

        return daySchedules.map((s, index) => {
            if (s) {
                return (
                    <div
                        key={`schedule-${s.id}`}
                        className={styles.scheduleBoxWrapper}
                    >
                        <ScheduleBox
                            key={`lgs-${s.id}`}
                            schedule={s}
                            onRemoveSchedule={onRemoveSchedule}
                            setScheduleToEdit={setScheduleToEdit}
                            onRemoveOfficeWindow={onRemoveOfficeWindow}
                            onSelectSchedule={onSelectSchedule}
                            onUpdateCaseLoad={onUpdateCaseLoad}
                            loggedInClinician={loggedInClinician}
                            officeSchedulesInReview={officeSchedulesInReview}
                            setRefreshKey={setRefreshKey}
                            onEditIntakeSession={onEditIntakeSession}
                            intakeSessionAvailability={intakeSessionAvailability}
                        />
                    </div>
                );
            }

            return (
                <div
                    key={`empty-${weekDay}-${index}`}
                    className={`${styles.scheduleBoxWrapper} ${styles.emptySlot}`}
                />
            );
        });
    };

    return (
        <div className={styles.calendarWrapper}>
            <EditScheduleDialog
                schedules={schedules}
                schedule={scheduleToEdit}
                setScheduleToEdit={setScheduleToEdit}
                onUpdateSchedule={(s) => {
                    onUpdateSchedule(s);
                    setScheduleToEdit(null);
                }}
                loggedInClinician={loggedInClinician}
            />
            <div className={styles.dummyDayWrapper}>
                <div className={styles.dummyDay}>
                    <span />
                </div>
                <div className={styles.emptyCalendarDayName} />
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Sun</div>
                {getScheduleListGroupByWeekday("sun")}
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Mon</div>
                {getScheduleListGroupByWeekday("mon")}
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Tue</div>
                {getScheduleListGroupByWeekday("tue")}
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Wed</div>
                {getScheduleListGroupByWeekday("wed")}
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Thur</div>
                {getScheduleListGroupByWeekday("thur")}
            </div>
            <div className={styles.calendarDayWrapper}>
                <div className={styles.calendarDayName}>Fri</div>
                {getScheduleListGroupByWeekday("fri")}
            </div>
            <div className={cn(styles.calendarDayWrapper, styles.lastDayWrapper)}>
                <div className={styles.calendarDayName}>Sat</div>
                {getScheduleListGroupByWeekday("sat")}
            </div>
        </div>
    );
}
